import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  Switch,
  Button,
  Tooltip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";

import { defaultStorefronts, personas as defaultPersonas } from "./test-data";

const getInitialStorefront = (user, storefronts) => {
  if (user?.storefront) return user.storefront;
  if (user?.storefrontId) return user.storefrontId;
  if (user?.storefrontNumber) return user.storefrontNumber;
  return storefronts[0]?.value || "";
};

const getInitialPersona = (user, personas) => {
  if (user?.persona) return user.persona;
  return personas[0] || "";
};

const UserProfileForm = ({
  selectedUser,
  storefronts = defaultStorefronts,
  personas = defaultPersonas,
  onChange,
  onSave,
  onClone,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [storefront, setStorefront] = useState("");
  const [persona, setPersona] = useState("");
  const [active, setActive] = useState(true);
  const [lastLogin, setLastLogin] = useState("");

  useEffect(() => {
    if (!selectedUser) return;
    setFirstName(selectedUser.firstName || "");
    setLastName(selectedUser.lastName || "");
    setSecondaryEmail(selectedUser.secondaryEmail || "");
    setStorefront(getInitialStorefront(selectedUser, storefronts));
    setPersona(getInitialPersona(selectedUser, personas));
    setActive(selectedUser?.active ?? true);
    setLastLogin(selectedUser?.lastLogin || "");
  }, [selectedUser, storefronts, personas]);

  useEffect(() => {
    if (!onChange) return;
    onChange({
      firstName,
      lastName,
      secondaryEmail,
      storefront,
      persona,
      active,
      lastLogin,
    });
  }, [
    firstName,
    lastName,
    secondaryEmail,
    storefront,
    persona,
    active,
    lastLogin,
    onChange,
  ]);

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
      <Box sx={{ maxWidth: 800 }}>
        {/* Profile Fields Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#333",
              fontWeight: 700,
              fontSize: "1.6rem",
              mb: 3,
              borderBottom: "2px solid #e0e0e0",
              pb: 1,
            }}
          >
            USER PROFILE
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1.3rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "1.3rem",
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1.3rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "1.3rem",
                  },
                }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Secondary Email"
                value={secondaryEmail}
                onChange={(e) => setSecondaryEmail(e.target.value)}
                type="email"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1.3rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "1.3rem",
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="All correspondence including quote and order email notifications sent to you while using FE Direct will also be sent to the email you have provided above."
                          placement="top"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: { fontSize: "1.3rem", lineHeight: 1.4 },
                            },
                          }}
                        >
                          <IconButton
                            edge="end"
                            tabIndex={-1}
                            aria-label="Secondary email info"
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: "1.3rem" }}>Storefront</InputLabel>

                <Select
                  label="Storefront"
                  value={storefront}
                  onChange={(e) => setStorefront(e.target.value)}
                  sx={{ fontSize: "1.3rem" }}
                >
                  {storefronts.map((s) => (
                    <MenuItem
                      key={s.value}
                      value={s.value}
                      style={{
                        fontSize: "1.3rem",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: "1.3rem" }}>Persona</InputLabel>

                <Select
                  label="Persona"
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  sx={{ fontSize: "1.3rem" }}
                >
                  {personas.map((p) => (
                    <MenuItem
                      key={p}
                      value={p}
                      style={{
                        fontSize: 14,
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Actions Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
              marginTop: 4,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              size="large"
              sx={{
                fontSize: "1.3rem",
              }}
              onClick={() => {
                const payload = {
                  firstName,
                  lastName,
                  secondaryEmail,
                  storefront,
                  persona,
                  active,
                  lastLogin,
                };
                if (onSave) onSave(payload);
                else console.log("Save Changes", payload);
              }}
            >
              Save
            </Button>

            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              size="large"
              sx={{
                fontSize: "1.3rem",
                backgroundColor: "#757575",
                color: "white",
                "&:hover": { backgroundColor: "#616161" },
              }}
              onClick={() => {
                const payload = {
                  firstName,
                  lastName,
                  secondaryEmail,
                  storefront,
                  persona,
                  active,
                  lastLogin,
                };
                if (onClone) onClone(payload);
                else console.log("Clone User", payload);
              }}
            >
              Clone
            </Button>
          </Box>
        </Paper>

        {/* Status Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#333",
              fontWeight: 700,
              fontSize: "1.6rem",
              mb: 3,
              borderBottom: "2px solid #e0e0e0",
              pb: 1,
            }}
          >
            USER STATUS
          </Typography>

          {/* Last Login */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#333", fontWeight: 700, fontSize: "1.4rem", mb: 1 }}
            >
              LAST LOGIN
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#555", fontSize: "1.35rem" }}
            >
              {lastLogin ? lastLogin : "Not available"}
            </Typography>
          </Box>

          {/* Active Toggle - Redesigned */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#333",
                fontWeight: 700,
                fontSize: "1.4rem",
                mb: 1.5,
              }}
            >
              ACCOUNT STATUS
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                backgroundColor: active ? "#e8f5e8" : "#f5f5f5",
                borderRadius: 2,
                border: `2px solid ${active ? "#4caf50" : "#e0e0e0"}`,
                transition: "all 0.3s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: active ? "#4caf50" : "#9e9e9e",
                    transition: "all 0.3s ease",
                  }}
                />

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    color: active ? "#2e7d32" : "#666",
                  }}
                >
                  {active ? "Active User" : "Inactive User"}
                </Typography>
              </Box>

              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: "#4caf50",
                      "& + .MuiSwitch-track": {
                        backgroundColor: "#4caf50",
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    borderRadius: 7,
                  },
                }}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                fontSize: "1.1rem",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {active
                ? "User can access the system"
                : "User access is disabled"}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default UserProfileForm;
